import { useEffect, useId, useRef, useState } from "react";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { ChevronDown } from "lucide-react";
import { useLocation } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { saveScope, getAppSettings } from "@/lib/api";
import {
  queryClient,
  publicAppSettingsKey,
  removeAllScopedFromCache,
  unscopedKey,
} from "@/lib/queryClient";
import { revokeAllPlayerPhotos } from "@/features/player-photos/usePlayerPhoto";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TeamSwitcher } from "./TeamSwitcher";
import { hostKind } from "@/lib/hostKind";
import { ClubMark } from "@/components/ClubMark";

function useSeasonSelection() {
  return useMutation({
    mutationFn: saveScope,
    onSuccess: (payload) => {
      useAuthStore.getState().applyMePayload(payload);
      useScopeStore.getState().applyMePayload(payload);
      queryClient.setQueryData(unscopedKey("me"), payload);
      removeAllScopedFromCache();
      revokeAllPlayerPhotos();
    },
  });
}

export function isCoachingWorkspacePath(pathname: string): boolean {
  return /^\/coach(?:\/|$)/.test(pathname);
}

/** Authoritative scope lives in the existing store and server; no Apply buffer. */
export function GlobalScopeBar() {
  const { pathname } = useLocation();
  const settings = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const me = useAuthStore((s) => s.me);
  const active = useScopeStore((s) => s.active);
  const mutation = useSeasonSelection();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const popupId = useId();
  const clubName =
    settings.data?.settings?.club_title ||
    me?.current_club?.name ||
    "Your club";
  const clubIconUrl = settings.data?.assets?.favicon_url ?? "";
  const clubLogoUrl = settings.data?.assets?.club_logo_url ?? "";
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  // The scope control belongs to the coaching workspace. Public team pages
  // carry their own URL-backed season selector, while club administration
  // owns its context inside AdminShell; rendering this bar on either surface
  // creates a second, conflicting source of navigation state.
  if (hostKind() !== "club" || !isCoachingWorkspacePath(pathname))
    return null;
  if (!me?.user) return null;
  return (
    <section
      aria-label="Team and season"
      className="global-scope-bar coach-workspace-scope"
      data-testid="global-scope-bar"
    >
      <div className="global-scope-control">
        <button
          ref={trigger}
          type="button"
          aria-label="Choose team and season"
          aria-expanded={open}
          aria-controls={popupId}
          onClick={() => setOpen((v) => !v)}
          className="global-scope-trigger"
        >
          <ClubMark
            name={clubName}
            imageUrl={clubIconUrl}
            fallbackImageUrl={clubLogoUrl}
            className="global-scope-club-mark"
          />
          <span className="coach-workspace-scope-copy">
            <span>{active?.team.name ?? "Choose team"}</span>
            <span>{active?.season?.name ?? "Choose season"}</span>
          </span>
          <ChevronDown className="size-4 shrink-0" aria-hidden />
        </button>
        {open ? (
          <CoachModal
            title="Team & season"
            closeLabel="Close team and season"
            description="Choose the context for your workspace."
            onClose={() => setOpen(false)}
            returnFocusTo={trigger.current}
          >
            <p className="scope-role mb-3 text-xs text-ink-muted">
              {(me.is_club_admin
                ? "Club administrator"
                : (me.teams.find((t) => t.id === active?.team.id)?.membership_role ?? "")
              ).replace(/_/g, " ")}
            </p>
            <div
              id={popupId}
              className="global-scope-fields"
              role="group"
              aria-label="Team and season selection"
            >
              <div>
                <p className="mb-1 text-xs text-ink-muted">Team</p>
                <TeamSwitcher showSingleTeam />
              </div>
              {active?.team && me.seasons.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs text-ink-muted">Season</p>
                  <TkticSelect
                    ariaLabel="Season"
                    value={active.season?.id ?? ""}
                    options={me.seasons.map((season) => ({
                      value: season.id,
                      label: season.name,
                    }))}
                    disabled={mutation.isPending}
                    onChange={(seasonId) =>
                      mutation.mutate({
                        team_id: active.team.id,
                        season_id: seasonId,
                      })
                    }
                  />
                </div>
              ) : (
                <p className="text-sm text-ink-muted">
                  Choose a team to see its seasons.
                </p>
              )}
              {mutation.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {mutation.error.message}
                </p>
              ) : null}
            </div>
          </CoachModal>
        ) : null}
      </div>
    </section>
  );
}
